import Error from "@common/components/Atoms/Error";
import { FormHandler, Toggle } from "@common/components/Atoms/FormInputs";
import { HelpButton } from "@common/components/Atoms/HelpButton";
import { FormField } from "@common/models";
import { SettingsService } from "@pro/_services";
import { SMSOptions } from "@pro/containers/Modals/SMSOptions";
import * as React from "react";

interface NotificationsProps {
    updating: (value: boolean) => void;
}

interface NotificationsState {
    showError: boolean;
    errorMessage: string;
    showSMSOptionIsOpen: boolean;
    notificationFields: {
        is_agent_active: FormField;
        is_agent_immediate: FormField;
        is_sms_allowed: FormField;
    };
}

export class Notifications extends React.Component<NotificationsProps, NotificationsState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.state = {
            showError: false,
            errorMessage: "",
            showSMSOptionIsOpen: false,
            notificationFields: {
                is_agent_active: {
                    name: "is_agent_active",
                    label: "is agent active",
                    value: false,
                    isValid: true,
                    validationMessages: [],
                    rules: [],
                },
                is_agent_immediate: {
                    name: "is_agent_immediate",
                    label: "is agent active",
                    value: false,
                    isValid: true,
                    validationMessages: [],
                    rules: [],
                },
                is_sms_allowed: {
                    name: "is_sms_allowed",
                    label: "is agent active",
                    value: false,
                    isValid: true,
                    validationMessages: [],
                    rules: [],
                },
            },
        };

        this.onNotificationsChange = this.onNotificationsChange.bind(this);
        this.loadNotificationSettings = this.loadNotificationSettings.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key => abortControllers[key].abort());
        }
    }

    onNotificationsChange(name, value) {
        // no validation required...
        const fields = this.state.notificationFields;
        FormHandler.handleInputChange(name, value, fields);

        this.setState(
            {
                showError: false,
                errorMessage: "",
                notificationFields: fields,
            },
            () => {
                this.props.updating(true);
                const obj = FormHandler.getAllValuesAsObject(fields);
                SettingsService.saveNotifications(obj)
                    .then(() =>
                        this.setState({ notificationFields: fields }, () => {
                            this.props.updating(false);
                        })
                    )
                    .catch(err =>
                        this.setState(
                            {
                                showError: true,
                                errorMessage: err.message + " Please try again.",
                            },
                            () => {
                                this.props.updating(false);
                            }
                        )
                    );
            }
        );
    }

    loadNotificationSettings() {
        this.props.updating(true);

        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.getNotificationSettings) {
            this._abortControllers.getNotificationSettings.abort();
        }
        this._abortControllers.getNotificationSettings = new AbortController();
        const signal = this._abortControllers.getNotificationSettings.signal;

        SettingsService.getNotificationSettings(signal)
            .then(settings => {
                this.setState(prevState => {
                    const fields = prevState.notificationFields;
                    FormHandler.setFieldValuesFromObject(fields, settings, false);
                    this.props.updating(false);
                    return { notificationFields: fields };
                });
            })
            .catch(err => {
                if (err.type === "abort" || err.name === "AbortError") {
                    this.props.updating(false);
                    return;
                }

                this.setState({ showError: true, errorMessage: err.message }, () => {
                    this.props.updating(false);
                });
            });
    }

    closeModal() {
        this.setState({ showSMSOptionIsOpen: false });
    }

    openModal() {
        this.setState({ showSMSOptionIsOpen: true });
    }

    componentDidMount() {
        this.loadNotificationSettings();
    }

    render() {
        return (
            <section className="c-settings__pane">
                <h2 className="c-settings__title">notifications</h2>

                <Error
                    show={this.state.showError}
                    title="error saving notifications"
                    description={this.state.errorMessage}
                />

                <Toggle
                    name="is_agent_active"
                    label="Available for Referral Opportunities"
                    description="Send me weekly emails when new jobs that match my profile are available."
                    value={this.state.notificationFields.is_agent_active.value}
                    isValid={this.state.notificationFields.is_agent_active.isValid}
                    validationMessages={
                        this.state.notificationFields.is_agent_active.validationMessages
                    }
                    onChange={this.onNotificationsChange}
                />
                <Toggle
                    name="is_agent_immediate"
                    label="Immediate Notification"
                    description="Notify me immediately of new job referral opportunities."
                    value={this.state.notificationFields.is_agent_immediate.value}
                    isValid={this.state.notificationFields.is_agent_immediate.isValid}
                    validationMessages={
                        this.state.notificationFields.is_agent_immediate.validationMessages
                    }
                    onChange={this.onNotificationsChange}
                />
                <Toggle
                    name="is_sms_allowed"
                    label="Text Messages"
                    infoButton={<HelpButton small={true} onClick={this.openModal} />}
                    description="If I can't be reached via phone, I want to receive support via text messages."
                    value={this.state.notificationFields.is_sms_allowed.value}
                    isValid={this.state.notificationFields.is_sms_allowed.isValid}
                    validationMessages={
                        this.state.notificationFields.is_sms_allowed.validationMessages
                    }
                    onChange={this.onNotificationsChange}
                />
                <SMSOptions
                    isOpen={this.state.showSMSOptionIsOpen}
                    onOkay={this.closeModal}
                    onCancel={this.closeModal}
                    closeModal={this.closeModal}
                />
            </section>
        );
    }
}
